import { type Chart, type ChartOptions } from "chart.js";
import moment, { type Moment } from "moment";

import {
  type BenchmarkData,
  type ChartDataset,
  type MetricData,
  type NorthbeamIndexData,
  type TileDescriptor,
  type TileFilterQuery,
} from "@features/metrics-explorer/types";
import { isIncomplete } from "@features/sales-v3/charts/plugins/chart-data-manager-plugin";
import {
  COMPARE_METRIC_1_LINE_DASH,
  generateDateLabels,
} from "@features/sales-v3/utils/chart";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { type User } from "@internal/user";
import { type SalesMetricV2 } from "@north-beam/nb-client-side/metrics-v2";
import {
  MCAttributionWindowDays,
  NBAccountingMode,
  convertStartDateToTimeZone,
  formatting,
  type CurrencyCode,
  type DateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";

import { chartTooltip } from "./chart-tooltip";

function getCssVariable(name: string, from: HTMLElement = document.body) {
  return getComputedStyle(from).getPropertyValue(name).trim();
}

export function getThemeColors() {
  return {
    borderTertiary: getCssVariable("--v2-border-tertiary"),
    textPrimary: getCssVariable("--v2-text-primary"),
    backgroundPrimary: getCssVariable("--v2-bg-primary"),
  };
}

type GetChartOptonsParams = {
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularityV2;
  filterQuery: TileFilterQuery | null;
  alignAxes: boolean;
  code: CurrencyCode;
  data: ReturnType<typeof generateChartData>;
  user: User;
  themeColors: {
    borderTertiary: string;
    textPrimary: string;
    backgroundPrimary: string;
  };
};

export function getChartOptions({
  attributionModel,
  attributionWindow,
  accountingMode,
  filterQuery,
  timeGranularity,
  alignAxes,
  data,
  code,
  user,
  themeColors,
}: GetChartOptonsParams): ChartOptions<any> {
  const scales: Record<string, any> = {
    x: {
      grid: {
        drawOnChartArea: false,
        drawBorder: false,
        drawTicks: false,
      },
      ticks: {
        maxTicksLimit: 12,
        padding: 12,
        maxRotation: 20,
        autoSkip: true,
        color: themeColors.textPrimary,
        font: {
          size: 10,
        },
      },
      type: "timeseries",
      time: {
        unit: (
          {
            hourly: "hour",
            daily: "day",
            weekly: "week",
            monthly: "month",
          } as const
        )[timeGranularity],
        displayFormats: {
          hour: "MMM D, hA",
        },
      },
    },
  };

  const groupedDatasets = groupDatasets(data.datasets);
  let globalMaxYValue = 0;
  let index = -1;

  for (const { datasets } of Object.values(groupedDatasets)) {
    const dataPoints: number[] = [];

    for (const dataset of datasets) {
      if (dataset.datasetType === "metric" && !dataset.hidden) {
        for (const [index, point] of dataset.data.entries()) {
          dataPoints[index] ||= 0;

          if (dataset.tile.isStacked) {
            dataPoints[index] += point.y ?? 0;
          } else {
            dataPoints[index] = Math.max(dataPoints[index], point.y ?? 0);
          }
        }
      }
    }

    const maxVal = Math.max(...dataPoints);

    if (maxVal > globalMaxYValue) {
      globalMaxYValue = maxVal;
    }
  }

  for (const [groupId, { datasets }] of Object.entries(groupedDatasets)) {
    const isScaleVisible = datasets.some(
      (e) => e.datasetType === "metric" && !e.hidden,
    );

    if (isScaleVisible) {
      index += 1;
    }

    const isFirstScale = index === 0;
    const isTitleVisible = index < 3;

    let maxYValueDataset: number | null = null;

    if (datasets.length > 1) {
      maxYValueDataset = Math.max(
        ...datasets.map((dataset) =>
          Math.max(...dataset.data.map((e) => e.y ?? 0)),
        ),
      );
    }

    if (alignAxes) {
      maxYValueDataset = globalMaxYValue;
    }

    scales[`y:${groupId}`] = {
      position: isFirstScale ? "left" : "right",
      display: isScaleVisible,
      stacked: datasets.some((e) => e.tile.isStacked),
      beginAtZero: true,
      grid: {
        display: false,
        drawTicks: false,
        drawBorder: false,
        drawOnChartArea: true,
      },
      ticks: {
        display: isTitleVisible,
        maxTicksLimit: 6,
        padding: 10,
        color: themeColors.textPrimary,
        font: {
          size: 10,
        },
        callback: (value: number | string) => {
          const val = Number(value);

          return formatting.formatMetric(val, datasets[0].metric.format, {
            code,
            mantissa: val > 0 && val < 5 ? 1 : 0,
            average: true,
          });
        },
      },
      title: {
        display: isTitleVisible,
      },
      ...(maxYValueDataset ? { suggestedMax: maxYValueDataset } : null),
    };
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animations: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: chartTooltip,
        callbacks: {
          label(context: any) {
            const tile = context.dataset.tile;

            return {
              datasetIndex: context.datasetIndex,
              label: context.dataset.name,
              dateRange: context.parsed.dateRange,
              attributionModel: user.attributionMethodEnum.getLabel(
                tile.attributionModel ?? attributionModel,
              ),
              attributionWindow: MCAttributionWindowDays.getLabel(
                tile.attributionWindow ?? attributionWindow,
              ),
              accountingMode: NBAccountingMode.getLabel(
                tile.accountingMode ?? accountingMode,
              ),
              accountingModeRaw: tile.accountingMode ?? accountingMode,
              timeGranularity,
              filterQuery: tile.filterQuery ?? filterQuery,
              value: formatting.formatMetric(
                context.parsed.y,
                context.dataset.metric.format,
                { code, precision: "approximate" },
              ),
            };
          },
          labelColor(context: any) {
            return {
              backgroundColor: context.dataset.labelColor,
              borderDash: context.dataset.borderDash,
            };
          },
        },
      },
      legend: {
        display: false,
      },
    },
    barThickness: "flex",
    minBarLength: 3,
    scales,
  };
}

export function generateChartData({
  metricsData,
  benchmarksData,
  indexData,
  tiles,
  attributionModel,
  attributionWindow,
  accountingMode,
  timeGranularity,
  dateRange,
  timezone,
  buildMetrics,
}: {
  metricsData: MetricData[];
  benchmarksData: BenchmarkData[];
  indexData: NorthbeamIndexData[];
  tiles: TileDescriptor[];
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  timeGranularity: ITimeGranularityV2;
  dateRange: DateInterval;
  timezone: User["timezone"];
  buildMetrics(value: {
    attributionModel: string;
    attributionWindow: IMCAttributionWindowDays;
    accountingMode: INBAccountingMode;
  }): SalesMetricV2[];
}) {
  const today = moment().startOf("day");
  const datasets: ChartDataset[] = [];

  let longestDaysDiff = moment(dateRange.endDate).diff(
    dateRange.startDate,
    "days",
  );

  for (const tile of tiles) {
    if (tile.compareDateRange) {
      const tileDateRange = deriveComparisonDateRange(
        dateRange,
        tile.compareDateRange,
        timezone,
        timeGranularity,
      );

      const daysDiff = tileDateRange
        ? moment(tileDateRange.endDate).diff(tileDateRange.startDate, "days")
        : 0;

      if (daysDiff > longestDaysDiff) {
        longestDaysDiff = daysDiff;
      }
    }
  }

  const startDate = dateRange.startDate;

  const endDate = moment(startDate)
    .add(longestDaysDiff, "days")
    .format("YYYY-MM-DD");

  const labels = generateDateLabels(
    { startDate, endDate },
    timezone,
    timeGranularity,
  );

  for (const metricData of metricsData) {
    const tile = tiles.find((e) => e.id === metricData.tileId);

    if (!tile) {
      continue;
    }

    const metrics = buildMetrics({
      attributionModel: tile.attributionModel ?? attributionModel,
      attributionWindow: tile.attributionWindow ?? attributionWindow,
      accountingMode: tile.accountingMode ?? accountingMode,
    });

    const metric = metrics.find((e) => e.id === tile?.metricId);
    const data = metricData.data;

    if (!metric) {
      continue;
    }

    const datasetName = tile.name || metric.name;

    if (!data) {
      datasets.push({
        datasetType: "metric",
        data: [],
        name: datasetName,
        metric: metric,
        tile,
        hidden: false,
      });

      continue;
    }

    const dataset: ChartDataset = {
      datasetType: "metric",
      data: [],
      name: datasetName,
      metric: metric,
      tile,
      hidden: !tile.visible,
    };

    const dateFormat =
      timeGranularity === "hourly"
        ? "YYYY-MM-DDTHH:mm:ss.SSS[Z]"
        : "YYYY-MM-DD";

    for (const [index, row] of data.rows.entries()) {
      const date =
        timeGranularity === "hourly"
          ? moment(row.date ?? "").tz("UTC")
          : moment(convertStartDateToTimeZone(row.date ?? "", timezone));
      const endDate = getEndDate(date, timeGranularity);

      const value = metric.getValue(row.metrics) ?? 0;
      let y = isNaN(value) ? 0 : value;

      if (Math.abs(y) < 1e-6 || y === Infinity) {
        y = 0;
      }

      dataset.data.push({
        x: labels[index],
        y: timeGranularity === "hourly" ? y : date.isAfter(today) ? null : y,
        dateRange: {
          startDate: date.format(dateFormat),
          endDate: endDate.format(dateFormat),
        },
      });
    }

    datasets.push(dataset);

    const benchmark = benchmarksData.find((e) => e.tileId === tile.id);

    if (benchmark && benchmark.benchmark) {
      const benchmarkDataset: ChartDataset = {
        datasetType: "benchmark",
        data: [],
        name: `${datasetName} Benchmark`,
        metric: metric,
        tile,
        hidden: !tile.visible,
      };

      for (const label of labels) {
        const date =
          timeGranularity === "hourly"
            ? moment(label).tz("UTC")
            : moment(label);
        const endDate =
          timeGranularity === "hourly"
            ? moment(label).tz("UTC")
            : moment(label);

        if (timeGranularity === "hourly") {
          endDate.endOf("hour");
        }

        if (timeGranularity === "weekly") {
          endDate.endOf("week");
        }

        if (timeGranularity === "monthly") {
          endDate.endOf("month");
        }

        benchmarkDataset.data.push({
          x: label,
          y: benchmark.benchmark,
          dateRange: {
            startDate: date.format(dateFormat),
            endDate: endDate.format(dateFormat),
          },
        });
      }

      datasets.push(benchmarkDataset);
    }

    const nbxData = indexData.find(
      (e) => e.tileId === tile.id && e.type === "nbx",
    );

    if (nbxData && nbxData.data) {
      const indexDataset: ChartDataset = {
        datasetType: "northbeam-index",
        data: [],
        name: `${datasetName} NBX`,
        metric: metric,
        tile,
        hidden: !tile.visible,
      };

      for (const [index, row] of nbxData.data.entries()) {
        const date = moment(
          convertStartDateToTimeZone(row.date ?? "", timezone),
        );
        const endDate = getEndDate(date, timeGranularity);

        const value = row.value ?? 0;
        let y = isNaN(value) ? 0 : value;

        if (Math.abs(y) < 1e-6 || y === Infinity) {
          y = 0;
        }

        indexDataset.data.push({
          x: labels[index],
          y: date.isAfter(today) ? null : y,
          dateRange: {
            startDate: date.format(dateFormat),
            endDate: endDate.format(dateFormat),
          },
        });
      }

      datasets.push(indexDataset);
    }
  }

  return { labels: labels, datasets };
}

const designElementManager = new DesignElementManager("v2");

export function generateChartDataset(
  chart: Chart<"line">,
  data: ReturnType<typeof generateChartData>,
  globalDateRange: DateInterval,
  timeGranularity: ITimeGranularityV2,
  timezone: User["timezone"],
) {
  const datasets = [];

  let colorIndex = -1;
  let stackIndex = -1;
  let order = data.datasets.length;

  for (const [idx, dataset] of data.datasets.entries()) {
    const isBenchmark = dataset.datasetType === "benchmark";
    const isNorthbeamIndex = dataset.datasetType === "northbeam-index";
    const isStacked = dataset.tile.isStacked;

    if (isBenchmark && isStacked) {
      continue;
    }

    if (!isBenchmark && !isNorthbeamIndex) {
      colorIndex += 1;
    }

    if (!isStacked) {
      order -= 1;
    }

    if (isStacked) {
      stackIndex += 1;
    }

    const singleDataPoint = dataset.data.length === 1;
    const color = designElementManager.getColorForIndex(colorIndex);
    const colorTransparent = color + "50";
    const colorBackdrop = color + "20";
    const colorBackdropTransparent = color + "10";
    const gradient = isStacked
      ? createLinearGradient(
          chart.ctx,
          chart.chartArea.top,
          chart.chartArea.bottom,
          color,
        )
      : color;

    const isIncompleteDate = (date: Date, index: number) => {
      const tile = dataset.tile;

      const dateRange = tile.compareDateRange
        ? deriveComparisonDateRange(
            globalDateRange,
            tile.compareDateRange,
            timezone,
            timeGranularity,
          )
        : globalDateRange;

      return isIncomplete({
        date,
        dateRange,
        timeGranularity,
        index,
        total: data.labels.length,
      });
    };

    let borderWidth = 2;

    if (!isBenchmark && dataset.tile.active) {
      borderWidth = 3;
    }

    if (isNorthbeamIndex) {
      borderWidth = 1;
    }

    let borderDash: number[] = [];

    if (isBenchmark) {
      borderDash = [2, 6];
    } else if (isNorthbeamIndex) {
      borderDash = [9, 3];
    } else if (dataset.tile.compareDateRange) {
      borderDash = COMPARE_METRIC_1_LINE_DASH;
    }

    datasets.push({
      ...dataset,
      id: idx,
      yAxisID: `y:${dataset.tile.groupId ?? dataset.tile.id}`,
      order,
      pointRadius: singleDataPoint ? 3 : 0,
      pointHoverRadius: 3,
      pointHitRadius: 100,
      backgroundColor: color,
      borderColor: color,
      hoverBackgroundColor: color,
      hoverBorderColor: color,
      borderWidth,
      borderDash,
      borderJoinStyle: "round" as const,
      tension: 0.3,
      fill: isStacked
        ? stackIndex === 0
          ? "origin"
          : stackIndex - 1
        : undefined,
      segment: {
        backgroundColor: (ctx: any) => {
          if (isBenchmark) {
            return colorBackdrop;
          }

          if (isStacked) {
            return gradient;
          }

          return isIncompleteDate(
            ctx.p0.parsed.dateRange.startDate,
            ctx.p0DataIndex,
          ) ||
            isIncompleteDate(ctx.p1.parsed.dateRange.startDate, ctx.p1DataIndex)
            ? colorBackdropTransparent
            : colorBackdrop;
        },
        borderColor: (ctx: any) => {
          if (isBenchmark) {
            return color;
          }

          return isIncompleteDate(
            ctx.p0.parsed.dateRange.startDate,
            ctx.p0DataIndex,
          ) ||
            isIncompleteDate(ctx.p1.parsed.dateRange.startDate, ctx.p1DataIndex)
            ? colorTransparent
            : color;
        },
      },
      labelColor: color,
    });
  }

  return { ...data, datasets };
}

const TIME_GRANULARITY_TO_UNIT_OF_TIME: Record<
  string,
  moment.unitOfTime.DurationConstructor
> = {
  hourly: "hours",
  daily: "days",
  weekly: "weeks",
  monthly: "months",
};

function getEndDate(startDate: Moment, timeGranularity: string) {
  if (timeGranularity === "daily") {
    return startDate;
  }

  const unit = TIME_GRANULARITY_TO_UNIT_OF_TIME[timeGranularity];
  return moment(startDate).endOf(unit);
}

export function groupDatasets<T extends { name: string; tile: TileDescriptor }>(
  datasets: T[],
) {
  const groups: Record<string, { title: string; datasets: T[] }> = {};

  for (const dataset of datasets) {
    const datasetId = dataset.tile.groupId ?? dataset.tile.id;
    const title = dataset.tile.groupName
      ? dataset.tile.groupName
      : dataset.name;

    groups[datasetId] ||= { title, datasets: [] };
    groups[datasetId].datasets.push(dataset);
  }

  return groups;
}

const createLinearGradient = (
  ctx: CanvasRenderingContext2D,
  top: number,
  bottom: number,
  color: string,
) => {
  const gradient = ctx.createLinearGradient(0, bottom, 0, top);
  gradient.addColorStop(0.9, color + "A0");
  gradient.addColorStop(0, "#FFFFFFE0");

  return gradient;
};
